import { Injectable, inject } from "@angular/core";
import { Toast, ToastOptions, ToastRef, ToastService, ToastVariant } from "@odx/angular/components/toast";

@Injectable({
  providedIn: "root",
})
export class ToastHelper {
  private toastService = inject(ToastService);

  private static readonly MESSAGE_DURATION = 5000;
  private static readonly SUCCESS_DURATION = 10000;
  private static readonly ERROR_DURATION = 30000;

  private openToasts = new Map<string, ToastRef>();

  constructor() {
    this.toastService.onRemove$.subscribe((toastRef) => this.openToasts.delete(toastRef.item.title.toString()));
  }

  message(title: string, description?: string, keepUntilDismissed: boolean = false) {
    this.addToast(
      {
        title,
        description,
        variant: ToastVariant.DEFAULT,
      },
      { duration: keepUntilDismissed ? 0 : ToastHelper.MESSAGE_DURATION },
    );
  }

  success(title: string, description?: string) {
    this.addToast(
      {
        title,
        description,
        variant: ToastVariant.SUCCESS,
      },
      { duration: ToastHelper.SUCCESS_DURATION },
    );
  }

  error(title: string, description?: string, keepUntilDismissed: boolean = false, actions?: Toast["actions"]) {
    this.addToast(
      {
        title,
        description,
        actions,
        variant: ToastVariant.DANGER,
      },
      { duration: keepUntilDismissed ? 0 : ToastHelper.ERROR_DURATION },
    );
  }

  warning(title: string, description: string, label: string, action: () => Promise<void>) {
    this.addToast({
      title: title,
      description: description,
      actions: [
        {
          label: label,
          action: async () => await action(),
        },
      ],
      variant: ToastVariant.WARNING,
    });
  }

  errorOffline() {
    this.error(
      $localize`:@@toast.error.offline.header:Keine Verbindung zur Cloud`,
      $localize`:@@toast.error.offline.message:Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.`,
    );
  }

  dismiss(title: string) {
    this.closeOpenToast(title);
  }

  private addToast(toast: Toast, options: Partial<ToastOptions> = {}) {
    const title = toast.title.toString();
    this.closeOpenToast(title);
    const toastRef = this.toastService.add(toast, options);
    this.openToasts.set(title, toastRef);
  }

  /**
   * This prevents from duplicate toast messages
   *
   * @param title
   * @private
   */
  private closeOpenToast(title: string) {
    const toastRef = this.openToasts.get(title);
    if (toastRef) {
      this.toastService.remove(toastRef);
    }
  }
}
